'use client';

import dynamic from 'next/dynamic';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useAuthStore } from '@/stores/AuthStore';

import { ErrorFallback } from './ErrorFallback';
import { MainCarouselSkeleton } from './MainCarousel';
import { PersonalizedSectionSkeleton } from './PersonalizedSection';
import { PolicyDataBanner } from './PolicyDataBanner';
import { PopularPolicySkeleton } from './PopularPolicy';
import { RealtimeRegionInfoSkeleton } from './RealtimeRegionInfo';

const PersonalizedSection = dynamic(
  () => import('./PersonalizedSection').then((mod) => mod.PersonalizedSection),
  { ssr: false, loading: () => <PersonalizedSectionSkeleton /> },
);

const MainCarousel = dynamic(
  () => import('./MainCarousel').then((mod) => mod.MainCarousel),
  { loading: () => <MainCarouselSkeleton /> },
);

const PopularPolicySection = dynamic(
  () => import('./PopularPolicy').then((mod) => mod.PopularPolicySection),
  { loading: () => <PopularPolicySkeleton /> },
);

const RealtimeRegionInfoSection = dynamic(
  () =>
    import('./RealtimeRegionInfo').then((mod) => mod.RealtimeRegionInfoSection),
  { loading: () => <RealtimeRegionInfoSkeleton /> },
);

/** 비로그인 시 PolicyDataBanner 렌더링 — 인증 확정 전까지 렌더링하지 않아 CLS 방지 */
export const AuthGatedPolicyDataBanner = () => {
  const isLogin = useAuthStore((state) => state.isLogin);

  if (isLogin === undefined || isLogin === true) {
    return null;
  }

  return <PolicyDataBanner />;
};

/** 로그인 시 PersonalizedSection 렌더링 — 인증 확정 전까지 스켈레톤 표시 */
export const AuthGatedPersonalizedSection = () => {
  const isLogin = useAuthStore((state) => state.isLogin);

  if (isLogin === undefined) {
    return <PersonalizedSectionSkeleton />;
  }

  if (isLogin !== true) {
    return null;
  }

  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <Suspense fallback={<PersonalizedSectionSkeleton />}>
        <PersonalizedSection />
      </Suspense>
    </ErrorBoundary>
  );
};

/** 메인 캐러셀 - 인증 분기 */
export const AuthGatedMainCarousel = () => {
  const isLogin = useAuthStore((state) => state.isLogin);

  // 인증 상태가 확정될 때까지 스켈레톤 표시
  if (isLogin === undefined) {
    return <MainCarouselSkeleton />;
  }

  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <Suspense fallback={<MainCarouselSkeleton />}>
        <MainCarousel />
      </Suspense>
    </ErrorBoundary>
  );
};

/** 인기 정책 - 인증 분기 */
export const AuthGatedPopularPolicy = () => {
  const isLogin = useAuthStore((state) => state.isLogin);

  // 인증 상태가 확정될 때까지 스켈레톤 표시
  if (isLogin === undefined) {
    return <PopularPolicySkeleton />;
  }

  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <Suspense fallback={<PopularPolicySkeleton />}>
        <PopularPolicySection />
      </Suspense>
    </ErrorBoundary>
  );
};

/** 지역 인기 소식 - 인증 분기 */
export const AuthGatedRealtimeRegionInfo = () => {
  const isLogin = useAuthStore((state) => state.isLogin);

  // 인증 상태가 확정될 때까지 스켈레톤 표시
  if (isLogin === undefined) {
    return <RealtimeRegionInfoSkeleton />;
  }

  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <Suspense fallback={<RealtimeRegionInfoSkeleton />}>
        <RealtimeRegionInfoSection />
      </Suspense>
    </ErrorBoundary>
  );
};
